import { Component, Input, OnChanges, OnDestroy } from '@angular/core';

import { Subscription } from 'rxjs';

import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { DirectionalCell } from '../models/directional-cell.model';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { ShiftSchedule } from 'src/shared/models/shift-schedule.model';
import { StaffingGridType, StaffingGridConstants } from '../utilities/staffing-grid-constants.util';
import { StaffingGridUtil } from '../utilities/staffing-grid.util';
import { ShiftScheduleService } from 'src/shared/services/shift-schedule.service';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';

@Component({
    selector: 'smt-base-grid',
    templateUrl: './base-grid.component.html',
    styleUrls: ['./base-grid.component.scss']
})
export class BaseGridComponent implements OnChanges, OnDestroy {
    @Input() costCenter: CostCenter; 
    @Input() startHour: number
    @Input() type: string;
    @Input() schedules: ShiftSchedule[];
    @Input() readOnly: boolean;

    copySource: ShiftSchedule;
    gridType: StaffingGridType;
    shiftSchedule: ShiftSchedule;    

    buildGrid:() => DirectionalCell[][];    
    grid: DirectionalCell[][] = [];
    initialize: Function;
    
    constructor(private _baseGridCostCenterConfigurationService: CostCenterConfigurationService) {                
    }

    ngOnChanges() {        
        this.Process();
    }

    ngOnDestroy() {
        this.buildGrid = null;
        this.initialize = null;                
    }

    createSchedule(): ShiftSchedule {
        let newShiftSchedule = new ShiftSchedule();

        newShiftSchedule.costCenterId = this.costCenter.id;
        newShiftSchedule.createdDate = new Date();
        newShiftSchedule.blockLength = StaffingGridConstants.DefaultShiftBlockLength;
        newShiftSchedule.shiftBlocks = null;
        newShiftSchedule.startHour = this.startHour;
        newShiftSchedule.type = StaffingGridUtil.getGridTypeDescription(this.gridType);
        newShiftSchedule.isSaturday = StaffingGridUtil.isGridTypeSaturday(this.gridType);
        newShiftSchedule.isSunday = StaffingGridUtil.isGridTypeSunday(this.gridType);
        newShiftSchedule.isWeekday = StaffingGridUtil.isGridTypeWeekday(this.gridType) || StaffingGridUtil.isGridTypeAllWeek(this.gridType);
        newShiftSchedule.isWeekend = StaffingGridUtil.isGridTypeWeekend(this.gridType) || StaffingGridUtil.isGridTypeAllWeek(this.gridType);

        return newShiftSchedule;
    }

    inactivateShiftSchedule(effectiveDate?: Date) {
        if(this.shiftSchedule) {
            this.shiftSchedule.inactiveDate = effectiveDate || new Date();
        }
    }

    async loadActiveSchedule(): Promise<ShiftSchedule> {
        let activeSchedules = this.schedules.filter(s => !s.inactiveDate);

        let activeSchedule: ShiftSchedule;

        if (StaffingGridUtil.isGridTypeAllWeek(this.gridType)) {
            activeSchedule = activeSchedules.find(s => s.isWeekday && s.isWeekend);
        }
        else if (StaffingGridUtil.isGridTypeWeekday(this.gridType)) {
            activeSchedule = activeSchedules.find(s => s.isWeekday);
            this.copySource = activeSchedules.find(s => s.isWeekend);
        }
        else if (StaffingGridUtil.isGridTypeWeekend(this.gridType)) {
            activeSchedule = activeSchedules.find(s => s.isWeekend);
            this.copySource = activeSchedules.find(s => s.isWeekday);
        }
        else if (StaffingGridUtil.isGridTypeSaturday(this.gridType)) {
            activeSchedule = activeSchedules.find(s => s.isSaturday);
            this.copySource = activeSchedules.find(s => s.isSunday);
        }
        else if (StaffingGridUtil.isGridTypeSunday(this.gridType)) {
            activeSchedule = activeSchedules.find(s => s.isSunday);
            this.copySource = activeSchedules.find(s => s.isSaturday);
        }

        if (!activeSchedule) {
            activeSchedule = this.createSchedule();
        }

        return activeSchedule;
    }
    
    async Process() {        
        if (!this.buildGrid) {
            return;
        }        

        this._baseGridCostCenterConfigurationService.announceAdminActionLoading(true);

        this.setType();
        this.shiftSchedule = await this.loadActiveSchedule();     

        if (WorkflowPageUtil.hasInpatientConfiguration(this.costCenter) && !!this.startHour && this.shiftSchedule.startHour != this.startHour) {
            this.inactivateShiftSchedule();
            this.shiftSchedule = this.createSchedule();
        }

        await this.initialize();        
        
        this.grid = this.buildGrid();

        if (!this.grid || !this.grid.length) {
            this._baseGridCostCenterConfigurationService.announceAdminActionLoading(false);
            return;
        }

        this.findHorizontalAdjacency();
        this.findVerticalAdjacency();

        this._baseGridCostCenterConfigurationService.announceAdminActionLoading(false);
    }    

    initializeGridProcess(gridLoader: () => DirectionalCell[][], initializer: Function) {
        this.buildGrid = gridLoader;
        this.initialize = initializer; 
    }

    private findHorizontalAdjacency() {
        let previousCell: DirectionalCell;

        this.grid.forEach(row => {
            row.forEach(cell => {
                if (previousCell) {
                    cell.leftCellId = previousCell.id;
                    previousCell.rightCellId = cell.id;
                }

                previousCell = cell;
            });            
        });
    }

    private findVerticalAdjacency() {
        let previousRow: DirectionalCell[];

        this.grid.forEach(row => {
            let counter = 0;

            row.forEach(cell => {                
                let aboveCell: DirectionalCell;

                if (previousRow) {
                    aboveCell = previousRow[counter];
                    cell.aboveCellId = aboveCell.id;
                    aboveCell.belowCellId = cell.id;                    
                }

                ++counter;
            });
            

            previousRow = row ;
        });
    }

    

    private setType() {
        switch(this.type) {
            case StaffingGridConstants.AllWeekAddOns:
                this.gridType = StaffingGridType.AllWeekAddOns;
                break;
            case StaffingGridConstants.AllWeekFixed:
                this.gridType = StaffingGridType.AllWeekFixed;
                break;
            case StaffingGridConstants.AllWeekVariable:
                this.gridType = StaffingGridType.AllWeekVariable;
                break;
            case StaffingGridConstants.SaturdayAddOns:
                this.gridType = StaffingGridType.SaturdayAddOns;
                break;
            case StaffingGridConstants.SaturdayFixed:
                this.gridType = StaffingGridType.SaturdayFixed;
                break;
            case StaffingGridConstants.SaturdayVariable:
                this.gridType = StaffingGridType.SaturdayVariable;
                break;
            case StaffingGridConstants.SundayAddOns:
                this.gridType = StaffingGridType.SundayAddOns;
                break;
            case StaffingGridConstants.SundayFixed:
                this.gridType = StaffingGridType.SundayFixed;
                break;
            case StaffingGridConstants.SundayVariable:
                this.gridType = StaffingGridType.SundayVariable;
                break;
            case StaffingGridConstants.WeekdayAddOns:
                this.gridType = StaffingGridType.WeekdayAddOns;
                break;
            case StaffingGridConstants.WeekdayFixed:
                this.gridType = StaffingGridType.WeekdayFixed;
                break;     
            case StaffingGridConstants.WeekdayVariable:
                this.gridType = StaffingGridType.WeekdayVariable;
                break;
            case StaffingGridConstants.WeekendAddOns:
                this.gridType = StaffingGridType.WeekendAddOns;
                break;
            case StaffingGridConstants.WeekendFixed:
                this.gridType = StaffingGridType.WeekendFixed;
                break;            
            case StaffingGridConstants.WeekendVariable:
                this.gridType = StaffingGridType.WeekendVariable;
                break;
            default:
                this.gridType = StaffingGridType.None;
                break;
        }
    }
}
